/* eslint-disable no-nested-ternary */
import {
  Box,
  Button,
  Checkbox,
  Typography,
  Paper,
  Stack,
  LinearProgress,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { memo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { CustomDialogBox } from '../../../../components/CustomDialogBox';
import useDebouncedValue from '../../../../hooks/useDebouncedValue';
import { SearchBar } from '../../../../components/FormComponents';
import { InfoChip } from '../../../../components/CustomChip';
import { customEntityKeys, customObjectOperationTypes } from '../../constants';

const EntityItem = memo(
  ({
    id, labelText, secondary, checked, onToggle,
  }) => (
    <Paper
      onClick={() => onToggle(id, checked)}
      sx={{
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        width: '100%',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        backgroundColor: 'grey.100',
        color: checked ? 'primary.main' : 'initial',
        '&:hover': { backgroundColor: 'grey.200' },
      }}
    >
      <Checkbox
        size="small"
        checked={checked}
        onChange={() => onToggle(id, checked)}
        onClick={(e) => e.stopPropagation()}
      />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {labelText}
        </Typography>
        {secondary && (
        <Typography variant="caption" color="text.secondary">
          {secondary}
        </Typography>
        )}
      </Box>
    </Paper>
  ),
  (prev, next) => prev.checked === next.checked
    && prev.labelText === next.labelText
    && prev.secondary === next.secondary,
);

function DialogContentElement({
  entityKey,
  handleSelectAll,
  handleUnSelectAll,
  searchTerm,
  setSearchTerm,
  filteredData,
  loading,
  selectedIds,
  handleToggle,
}) {
  return (
    <Box>
      {/* Info chip only for Custom Objects */}
      {entityKey === customEntityKeys.CUSTOM_OBJECT && (
      <InfoChip
        sxProps={{ mb: 2 }}
        type="multiline"
        icon={<InfoIcon color="primary" />}
        label="In this step, please select the custom objects that should be part of your Feature. You can refine their scope in the next step, e.g. limit custom object components to specific CRUD types."
      />
      )}

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} mb={2}>
        <Button size="small" variant="contained" onClick={handleSelectAll}>
          Select All
        </Button>
        <Button size="small" variant="outlined" onClick={handleUnSelectAll}>
          Unselect All
        </Button>
      </Stack>

      {/* Search */}
      <SearchBar
        label="Search"
        value={searchTerm}
        data={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onClear={() => setSearchTerm('')}
      />

      {/* Entity List */}
      <Stack spacing={1} mt={2} sx={{ maxHeight: '45vh', overflowY: 'auto' }}>
        {loading ? <LinearProgress /> : filteredData.length > 0 ? (
          filteredData.map((obj) => {
            const id = obj.custom_entity_id || obj.feature_id;
            const labelText = obj.custom_entity_label || obj.feature_name;
            const secondary = obj.package_label;
            const checked = selectedIds.has(id);

            return (
              <EntityItem
                key={id}
                id={id}
                labelText={labelText}
                secondary={secondary}
                checked={checked}
                onToggle={() => handleToggle(id, checked)}
              />

            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary">
            No results found.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export default function PickCustomEntityDialog({
  open,
  onClose,
  onSave,
  initialSelected = [],
  initialNestedFeatures = [],
  entityKey,
  label,
  featureId,
}) {
  const featuresList = useSelector((state) => state.featuresList?.response?.responseArray) || [];
  const customEntityList = useSelector((state) => state.customEntityList?.customEntityList) || [];
  const customEntityLoading = useSelector((state) => state.customEntityList?.loading);
  // const [pickEntities, setPickEntities] = useState(entityKey === customEntityKeys.NESTED_FEATURE ? initialNestedFeatures : initialSelected);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  // Filtering
  const filteredData = entityKey === customEntityKeys.NESTED_FEATURE
    ? featuresList.filter((f) => f.feature_id !== featureId && f.feature_name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
    : customEntityList.filter((e) => e.custom_entity_label.toLowerCase().includes(debouncedSearchTerm.toLowerCase()));

  const [selectedIds, setSelectedIds] = useState(
    new Set(
      (entityKey === customEntityKeys.NESTED_FEATURE
        ? initialNestedFeatures.map((f) => f.nested_feature_id)
        : initialSelected.map((e) => e.custom_entity_id)),
    ),
  );

  const handleToggle = useCallback((id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const allIds = entityKey === customEntityKeys.NESTED_FEATURE
      ? featuresList.map((f) => f.feature_id)
      : customEntityList.map((e) => e.custom_entity_id);
    setSelectedIds(new Set(allIds));
  }, [entityKey, featuresList, customEntityList]);

  const handleUnSelectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleSave = useCallback(() => {
    let selectedObjects;
    if (entityKey === customEntityKeys.NESTED_FEATURE) {
      selectedObjects = featuresList
        .filter((f) => selectedIds.has(f.feature_id))
        .map((f) => ({
          nested_feature_id: f.feature_id,
          nested_feature_name: f.feature_name,
        }));
    } else {
      selectedObjects = customEntityList
        .filter((e) => selectedIds.has(e.custom_entity_id))
        .map((e) => (entityKey === customEntityKeys.CUSTOM_OBJECT
          ? { ...e, allEvents: true, operation_type: customObjectOperationTypes }
          : e));
    }

    onSave(entityKey, selectedObjects);
    onClose();
  }, [entityKey, featuresList, customEntityList, selectedIds, onSave, onClose]);

  return (
    <CustomDialogBox
      title={label}
      open={open}
      handleClose={onClose}
      content={(
        <DialogContentElement
          entityKey={entityKey}
          handleSelectAll={handleSelectAll}
          handleUnSelectAll={handleUnSelectAll}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          loading={customEntityLoading}
          filteredData={filteredData}
          selectedIds={selectedIds}
          handleToggle={handleToggle}
        />
      )}
      actions={{ title: 'Save', onClick: handleSave }}
    />
  );
}
